import { jsPDF } from 'jspdf';
import { formatCurrency, formatDate } from '@/utils/constants';
import { COMMERCIAL } from '@/components/forms/CreateNewBusinessForm/validationSchema';

export const generateBusinessFormPDF = (formData, isProspect) => {
	try {
		const doc = new jsPDF();
		const pageWidth = doc.internal.pageSize.width;
		const margin = 15;
		const maxWidth = pageWidth - margin * 2;
		let yPosition = 20;
		const lineHeight = 6;
		const rowSpacing = 8;

		// Format value helper
		const formatValue = (value) => {
			if (Array.isArray(value)) {
				if (value.length === 0) return 'None';
				return value.join(', ');
			}
			if (typeof value === 'boolean') return value ? 'Yes' : 'No';
			if (value instanceof Date) return value.toLocaleDateString();
			if (value && typeof value === 'object') {
				if (value.Code && value.Name) return `${value.Name} [${value.Code}]`;
				return JSON.stringify(value);
			}
			return value != null && value !== '' ? value.toString() : 'Not provided';
		};

		// Title with timestamp
		doc.setFontSize(16);
		doc.setTextColor(40, 40, 40);
		doc.text(`${isProspect ? 'Prospect' : 'New Business'} Application Form`, margin, yPosition);

		doc.setFontSize(8);
		doc.setTextColor(100, 100, 100);
		doc.text(
			`Generated ${formatDate(new Date(), true)} by ${formData.completedBy}`,
			margin,
			yPosition + 5,
		);
		yPosition += 15;

		// Table header
		doc.setFontSize(10);
		doc.setFont(undefined, 'bold');
		doc.setFillColor(245, 245, 245);
		doc.rect(margin, yPosition, maxWidth, 10, 'F');
		doc.text('Field', margin + 2, yPosition + 7);
		doc.text('Value', margin + maxWidth * 0.4 + 2, yPosition + 7);
		yPosition += 15;

		const fields = getFormFields(formData, isProspect);

		fields.forEach((field) => {
			if (yPosition > 270) {
				doc.addPage();
				yPosition = 20;
				doc.setFont(undefined, 'bold');
				doc.setFillColor(245, 245, 245);
				doc.rect(margin, yPosition, maxWidth, 10, 'F');
				doc.text('Field', margin + 2, yPosition + 7);
				doc.text('Value', margin + maxWidth * 0.4 + 2, yPosition + 7);
				yPosition += 15;
			}

			doc.setFont(undefined, 'bold');
			const labelLines = doc.splitTextToSize(field.label, maxWidth * 0.35);

			doc.setFont(undefined, 'normal');
			const formattedValue = formatValue(field.value);
			const valueLines = doc.splitTextToSize(formattedValue, maxWidth * 0.55);

			const linesNeeded = Math.max(labelLines.length, valueLines.length);
			const rowHeight = linesNeeded * lineHeight + rowSpacing;

			if (yPosition + rowHeight > 280) {
				doc.addPage();
				yPosition = 20;
				doc.setFont(undefined, 'bold');
				doc.setFillColor(245, 245, 245);
				doc.rect(margin, yPosition, maxWidth, 10, 'F');
				doc.text('Field', margin + 2, yPosition + 7);
				doc.text('Value', margin + maxWidth * 0.4 + 2, yPosition + 7);
				yPosition += 15;
			}

			labelLines.forEach((line, index) => {
				doc.text(line, margin + 2, yPosition + index * lineHeight);
			});

			valueLines.forEach((line, index) => {
				doc.text(line, margin + maxWidth * 0.4 + 2, yPosition + index * lineHeight);
			});

			doc.setDrawColor(240, 240, 240);
			doc.line(
				margin,
				yPosition + rowHeight - 4,
				pageWidth - margin,
				yPosition + rowHeight - 4,
			);
			yPosition += rowHeight;
		});

		return doc;
	} catch (error) {
		console.error('generateBusinessFormPDF | error', error);
		throw error;
	}
};

export const getFormFields = (data, isProspect) => {
	const fields = [];

	// Deal Details
	if (!isProspect) {
		fields.push(
			{ label: 'Deal name', value: data.folderName },
			{ label: 'Year folder', value: data.yearFolder },
			{ label: 'Deal overview', value: data.dealOverview },
		);
	}

	// Client Type
	fields.push(
		{
			label: 'New or existing client',
			value: data.isExisting ? 'Existing Client' : 'New Client',
		},
		{ label: 'Client type', value: data.isProspect ? 'Prospect' : 'Client' },
	);

	// Client Details
	fields.push(
		{ label: 'Client', value: data.clientName },
		{ label: 'Trading as', value: data.tradingAs },
		{ label: 'Assign to', value: data.ownerDocId },
	);

	// Address
	const addressParts = [data.address, data.suburb, data.city, data.postcode, data.country].filter(
		(part) => part && part.trim() !== '',
	);

	const fullAddress = addressParts.join(', ');
	fields.push(
		{ label: 'Company address', value: fullAddress || 'Not provided' },
		{ label: 'Street address', value: data.address },
		{ label: 'Suburb', value: data.suburb },
		{ label: 'Postcode', value: data.postcode },
		{ label: 'City', value: data.city },
		{ label: 'Country', value: data.country },
	);

	// Business Description
	fields.push({ label: 'Description of Business Activities', value: data.otherInfo });

	// Client Category
	fields.push({ label: 'Client Category', value: data.clientCategory?.join(', ') || 'None' });

	// Business Information
	fields.push(
		{ label: 'Website Address', value: data.websiteAddress },
		{ label: 'Industry', value: data.industry },
		{
			label: 'Standard Industry Code',
			value: data?.anzsicCode?.AutoComplete ?? data?.anzsicCode?.Name ?? 'None',
		},
		{ label: 'No. Of Employees', value: data.numberofemployees },
		{
			label: 'Annual Turnover',
			value: data.annualrevenue
				? formatCurrency({ num: data.annualrevenue, showDecimal: true })
				: data.annualrevenue,
		},
		{ label: 'Source Of Business', value: data.sourceOfBusiness },
	);

	// Contact Details
	const contact = data.contacts?.[0] || {};
	fields.push(
		{
			label: 'Primary contact',
			value:
				(data.firstName + ' ' + data.lastName).trim() ||
				(contact.firstName + ' ' + contact.lastName).trim() ||
				'Not provided',
		},
		{ label: 'First Name', value: data.firstName || contact.firstName || 'Not provided' },
		{ label: 'Last Name', value: data.lastName || contact.lastName || 'Not provided' },
		{ label: 'Phone Number', value: data.phoneNumber || contact.phoneNumber || 'Not provided' },
		{
			label: 'Alternate Phone Number',
			value: data.phoneNumber2 || contact.phoneNumber2 || 'Not provided',
		},
		{
			label: 'Date of Birth',
			value: data.dateOfBirth || contact.dateOfBirth || 'Not provided',
		},
		{
			label: 'Email Address',
			value: data.emailAddress || contact.emailAddress || 'Not provided',
		},
		{ label: 'Role', value: contact.role || data.role || 'Not provided' },
	);

	// Deal-specific fields (only for non-prospects)
	if (!isProspect) {
		fields.push(
			{
				label: 'Goals of Cover',
				value:
					data.goalsOfCover && data.goalsOfCover.length > 0
						? data.goalsOfCover.join(', ')
						: 'None',
			},
			{ label: 'Additional Goals of Cover', value: data.additionalGoalsOfCover },
			{
				label: 'Risk Type',
				value: data.clientType === COMMERCIAL ? 'Commercial' : 'Domestic',
			},
			{ label: 'Deal Amount', value: formatCurrency({ num: data.dealAmount ?? 0 }) },
			{ label: 'Estimated Close Date', value: data.estimatedCloseDate },

			{
				label: 'Risk Classes Considered',
				value:
					data.riskClasses && data.riskClasses.length > 0
						? data.riskClasses.join(', ')
						: 'None',
			},
			{
				label: 'Additional Specialist Insurance Products Discussed and Outcomes',
				value: data.additionalSpecialistProducts,
			},
			{
				label: 'Upload Files',
				value:
					data.files && data.files.length > 0
						? data.files.map((file) => file.name).join(', ')
						: 'No files',
			},
			{ label: 'Renewal Date', value: data.renewalDate },
			{ label: 'Current Premium', value: formatCurrency({ num: data.adviserFee ?? 0 }) },
		);
	}

	return fields;
};
